import { touchRequested } from "../input/touch";
import { UNREADABLE_FILE } from "../session/restore";

/*
 * The first frame: the title screen, and the settings behind it.
 *
 * Chunk U-6, plan §5 "U-6" and decision D10(a), from
 * `ui-lab/surfaces/main-menu.html` — the Witcher-3 composition the owner
 * pointed at: the game's name in small caps over a gold flourish, a left rail of
 * words with the chosen one in a gold hairline box, the select hint at the
 * rail's foot, and a thin frame around the whole screen.
 *
 * **Only what is true today is drawn** (D10, and it is the whole of the
 * decision): *begin*, *load* where a caller handed over a door to open, and
 * *settings*, plus *leave* where a window can actually close.
 *
 * *load* is the one of those that was **absent** when this screen shipped, on
 * that same rule — U-6 refused to grey it because "a greyed item is a promise"
 * and nothing in the client read a replay back. The owner made the promise
 * (P-8: *"should have a load option that takes a saved JSON"*), so the item is
 * here with a real door behind it: the browser's file chooser, and a simulation
 * that rebuilds the recorded world and re-runs the recorded commands
 * (`session/restore.ts`, `world/wasm.ts`'s `restoreWasmWorld`).
 *
 * **There is still no *continue***, and it is still absent rather than greyed:
 * nothing in this client persists a session by itself, so the only session to
 * come back to is a file somebody saved — which is what *load* is. A row that
 * offered to resume something the browser never kept would be the promise D10
 * refused to make.
 *
 * It is written as the book's older sibling and shares every one of
 * `hud/opening.ts`'s rules, because they are rules about *when*:
 *
 * - **It runs before the loop and before the controls exist**, which is what
 *   makes the keyboard handling below safe to write at all: nothing else is
 *   listening yet, so Enter cannot also be a jump and Escape cannot also drop a
 *   pointer lock that has never been taken (`main.ts` constructs `Controls`
 *   after the start menus resolve).
 * - **It is gated on the URL alone** ({@link titleRequested}), never on
 *   `import.meta.env.DEV` — O7's rule. `?title=0` is the same session without
 *   it, and it is a **harness switch** in the family of `?fixture=1`: the three
 *   drivers open a dozen sessions between them and only one of each needs to
 *   walk through this screen to prove it works (`web/README.md`, "Fixture
 *   mode").
 * - **Nothing on it is composed from the world.** The screen reads no source,
 *   asks the boundary nothing and holds no session; every string in it is one
 *   this file typed, which is why it is allowed to type them at all (the
 *   composing rule, plan §3 invariant 7).
 *
 * **What is behind it is the dark, and that is a refusal rather than an
 * omission.** The lab drifts a night town behind the scrim; this client draws
 * no frame at all until the start menus resolve — `main.ts` calls
 * `loop.start()` after them, `view.render()` is only ever called from inside
 * it, and the chunk meshes are uploaded in the same loop — so there is no town
 * to drift and no camera to yaw. The scrim below is written as the lab wrote it,
 * left-weighted and transparent on the right, so the day a frame *is* drawn
 * behind it the composition is already the one the owner pointed at
 * (`docs/decisions/ui.md` §7).
 *
 * The file holds both halves of the surface — the panel that draws a view, and
 * the flow that owns the cursor and the keys — because the chunk's fence is one
 * file. The division is kept as a division of functions: {@link TitleScreen}
 * knows nothing about keys and {@link showTheTitle} knows nothing about the DOM,
 * which is the same line `hud/panels/origins.ts` and `hud/origins.ts` are split
 * along.
 */

/** `?title=0` — the screen, declined. */
const TITLE_PARAM = "title";

/** Where the touch choice is remembered, per browser. One key, spelled once. */
const TOUCH_SETTING_KEY = "deep-world.touch";

/** The game's name, in the interface's own voice. */
const WORDMARK = "Deep World";

/**
 * What the first item says.
 *
 * **The lab's word was *a new world* and it is not shipped**, on the standing
 * rule that a string has to be true in every session (chunk U-4's argument for
 * keeping *close the book*: *"it is true in every session, which the
 * alternatives were not"*). The default link plays one pinned world — the
 * curated seed, filled into the query at boot (`session/world.ts`) — with two
 * centuries already behind it, and pressing this generates nothing: the world
 * was built while the loading line was up, and what happens next is the book,
 * the lives and a name. A session that *did* name a fresh `?seed=` would make
 * the lab's word true, which is exactly the problem — a menu item that is true
 * of some links and false of the default one is a menu item nobody can rely on.
 *
 * *begin* is true of all of them and promises nothing the next beat does not
 * deliver. It is also the word the book's own foot line used to carry
 * (*"enter begins"*), so it is this client's word rather than a new one.
 */
const BEGIN_WORD = "begin";

/**
 * The second item, drawn only where a caller handed over a door for it.
 *
 * *load* rather than *continue*, because it is what the door does: the browser
 * asks for a file and the simulation rebuilds the session in it. Nothing is
 * remembered between sessions, so there is nothing to *continue* — and the same
 * argument that keeps *leave* off a tab that cannot close keeps this off a build
 * that was handed no door ({@link canLeave}, {@link TitleDoors}).
 */
const LOAD_WORD = "load";

/** The third item. The lab's word, kept — it is what the page behind it is. */
const SETTINGS_WORD = "settings";

/**
 * The last item, drawn only where it can do anything ({@link canLeave}).
 *
 * On the web `window.close()` is refused for a tab somebody opened themselves,
 * and a row that did nothing would be worse than no row: this screen's whole
 * argument is that an item on it is a door that opens.
 */
const LEAVE_WORD = "leave";

/** The settings rows. Two, because two is what exists. */
const TOUCH_ROW_NAME = "touch controls";
const BACK_ROW_NAME = "back";

/**
 * What the touch row says under itself.
 *
 * Two clauses and both are facts a player would otherwise have to discover:
 * *automatic* is the ordinary case and is a question asked of the device
 * (`input/touch.ts`'s `touchMode`), and a change made here does **not** re-dress
 * the session it is made in. See {@link storeTouchChoice} for why that is the
 * honest shape rather than a shortcoming.
 */
const TOUCH_ROW_NOTE =
  "automatic asks the device · what is chosen here is in force from the next session";

/**
 * And what it says instead when the link has already decided.
 *
 * `?touch=1` / `?touch=0` wins over the setting and always will — it is the
 * override a driver and the owner reach for at a desk (`docs/decisions/input.md`
 * §2) — so the row says so rather than showing a value that is not in force.
 */
const TOUCH_ROW_OVERRIDDEN =
  "?touch= in the link is deciding this session · what is chosen here is in force without it";

/** The three words the touch row cycles through. A closed vocabulary, in order. */
const TOUCH_CHOICE_WORDS: Readonly<Record<TouchChoice, string>> = {
  auto: "automatic",
  on: "on",
  off: "off",
};

/** The order the row walks them in, so a press is always the next one. */
const TOUCH_CHOICE_ORDER: readonly TouchChoice[] = ["auto", "on", "off"];

/**
 * What the rail's foot says, on a keyboard.
 *
 * A key and what it does, in pairs, so the panel can draw the key as the kit's
 * `kbd` chip — chunk U-1's rule that *a prompt reads as press this → this
 * happens before it is read at all*. It is also the shape U-1 filed as wanted
 * from `SessionChrome.keys`, minted here because this surface needed it first.
 */
const RAIL_KEYS: readonly TitleHint[] = [
  { press: "↑", does: "" },
  { press: "↓", does: "move" },
  { press: "Enter", does: "select" },
];

/** The same, with the settings page's two extra gestures. */
const SETTINGS_KEYS: readonly TitleHint[] = [
  { press: "↑", does: "" },
  { press: "↓", does: "move" },
  { press: "←", does: "" },
  { press: "→", does: "change" },
  { press: "Esc", does: "back" },
];

/**
 * And the same two, for a session with no keyboard.
 *
 * **Composed as a gesture rather than as a button**, which is invariant 5's
 * spirit rather than its letter: the rule is that a touch hint names the button
 * that does the thing rather than typing a sentence about it
 * (`input/touch.ts`'s `touchCloseHint`), and there is no button — the pad does
 * not exist yet and `TOUCH_BUTTONS` has no row for a screen that runs before
 * `Controls`. What a finger has here is the item itself, so that is what the
 * hint names.
 */
const RAIL_TAPS: readonly TitleHint[] = [{ press: null, does: "tap a line to choose it" }];
const SETTINGS_TAPS: readonly TitleHint[] = [
  { press: null, does: "tap a line to change it · tap back to leave" },
];

/** Which of the two shapes the screen is in. */
export type TitlePage = "rail" | "settings";

/** The rail's items, by the door each one opens. */
export type TitleItem = "begin" | "load" | "settings" | "leave";

/** The settings page's rows. */
export type TitleRow = "touch" | "back";

/** What the touch setting is set to. `auto` is the ordinary case: ask the device. */
export type TouchChoice = "auto" | "on" | "off";

/**
 * What the screen settled on.
 *
 * Three outcomes rather than a boolean, for {@link OpeningRead}'s reason: a link
 * that declined the screen, a player who pressed *begin* and a session that came
 * out of a file are three different sessions, and a check that could not tell
 * them apart would pass on a build whose title screen had quietly stopped being
 * drawn.
 *
 * `"loaded"` is the one that changes what happens next: the world already
 * exists, the log already holds whatever birth it holds, and `main.ts` skips the
 * beats that would ask a restored session who it is.
 */
export type TitleRead = "declined" | "begun" | "loaded";

/**
 * The doors this screen opens that are not its own.
 *
 * One, and it is the shape every seam in this client takes: a callback in, a
 * refusal out. The screen owns the file chooser because that is DOM, and owns
 * nothing about what a file *is* — it hands the text over and draws whatever
 * sentence comes back (`session/restore.ts` composes them, `world/wasm.ts`
 * rebuilds the world, and the refusals are the simulation's own).
 */
export interface TitleDoors {
  /**
   * Restores a session from the text of a file, and answers with the refusal to
   * show or `null` once the world is standing.
   *
   * The text and nothing else: what the file was called is a fact about
   * somebody's disk, and a session that named it in the interface would be
   * saying something about the machine rather than about the world.
   *
   * Absent in a build with nowhere to put a world — a check drawing the panel,
   * a harness — and then the item is not drawn at all.
   */
  readonly load?: (text: string) => Promise<string | null>;
}

/** One key and what it does, or one phrase with no key at all. */
export interface TitleHint {
  /** The key's own legend, drawn as a `kbd` chip. `null` on a touchscreen. */
  readonly press: string | null;

  /** What pressing it does. `""` where two chips share one verb (↑ ↓ move). */
  readonly does: string;
}

/** One word on the rail. */
export interface TitleItemView {
  readonly item: TitleItem;
  readonly word: string;
}

/** One row of the settings page. */
export interface TitleRowView {
  readonly row: TitleRow;
  readonly name: string;

  /** The value on the right of the row, or `""` for a row that is a way out. */
  readonly value: string;

  /** The sentence under it, or `""`. */
  readonly note: string;
}

/** Everything the screen draws. See {@link TitleScreen.show}. */
export interface TitleView {
  readonly page: TitlePage;

  /** The rail's words. Drawn on both pages: the rail does not go away. */
  readonly items: readonly TitleItemView[];

  /** The settings rows, or empty on the rail. */
  readonly rows: readonly TitleRowView[];

  /**
   * Which line of the **live** list is chosen — the items on the rail, the rows
   * on the settings page. Clamped here, so a caller may count past the end.
   */
  readonly cursor: number;

  /** The foot line, as keys and what they do. */
  readonly keys: readonly TitleHint[];

  /**
   * One sentence under the rail, or `""`.
   *
   * The refused file, and nothing else has ever needed one: the screen says
   * what is wrong with what the player chose, in the sans hand, and stays up so
   * they can choose again (`session/restore.ts`'s `refusedLine`).
   */
  readonly said: string;

  /**
   * A file was chosen at the load door, and this is its text.
   *
   * The screen reads the file because a file is a browser thing; what is in one
   * is somebody else's question entirely ({@link TitleDoors}).
   */
  readonly onFile: (text: string) => void;

  /**
   * A word on the rail was pressed, and it means the door it names.
   *
   * By the door rather than by an index, and that is the reason there are two
   * callbacks here rather than one: the rail stays on screen while the settings
   * page is open, so an index would be ambiguous between two lists on the one
   * page where a mistake would matter — pressing *begin* and starting a session
   * because the cursor happened to be on a settings row.
   */
  readonly onItem: (item: TitleItem) => void;

  /** Takes the chosen line of the settings page. */
  readonly onChoose: (at: number) => void;

  /** Moves the cursor without taking anything. */
  readonly onCursor: (at: number) => void;
}

/**
 * The screen itself: the scrim, the frame, the rail and the settings column.
 *
 * **Not a `.hud__panel`**, which is chunk U-2's structural claim applied one
 * surface further on and the same call `ui/opening.css` made for the opening's
 * beats: the chassis in `ui/panel.css` is a box in a corner with a plate and a
 * padding, and this is a full-frame state that owns everything on the glass. So
 * `ui/title.css` states the `[hidden]` rule the chassis would have lent it — an
 * author `display` beats the user agent's `[hidden]` however specific either is,
 * because the cascade sorts by origin first.
 *
 * **It is `hud__title*` and never `hud__menu*`**, which is not a style
 * preference: `hud__menu*` is chunk U-3's in-game tab screen, asserted by string
 * in the check tree and in three drivers (plan §3 invariant 9), and two surfaces
 * sharing a name prefix is the day somebody's `querySelector(".hud__menu-item")`
 * starts answering about a title screen.
 */
export class TitleScreen {
  /** The layer, and the whole of what `[hidden]` is asked about. */
  private readonly element: HTMLElement;

  /** The left-weighted wash. Its own element so the rail is not painted on it. */
  private readonly scrim: HTMLElement;

  private readonly rail: HTMLElement;
  private readonly itemsElement: HTMLElement;
  private readonly pageElement: HTMLElement;
  private readonly hintElement: HTMLElement;

  /** Where a refused file's sentence goes, and the only line this screen says. */
  private readonly saidElement: HTMLElement;

  /**
   * The file chooser, hidden, and the only way a browser will open one.
   *
   * A `<input type="file">` clicked from inside the gesture that asked for it:
   * there is no other API for this, and a chooser opened outside a real click or
   * key press is one the browser refuses. It lives in the layer so it goes with
   * it, and it is the one element on this screen that is never drawn.
   */
  private readonly fileElement: HTMLInputElement;

  /**
   * The words the rail is currently drawn with, so a *move* can be told from a
   * *new list*.
   *
   * `OriginsPanel`'s rule and its bug (owner report 2026-08-24: *"clicking the
   * offered lives does nothing"*): a browser raises no `click` on a node that
   * was taken out of the document between the press and the release. The cursor
   * follows the pointer on this screen — a rail of three words does not lurch,
   * so the strip's reason for refusing hover does not apply — which means every
   * `mouseenter` would otherwise rebuild the list under the finger that is about
   * to press it.
   */
  private drawn = "";

  /** Which row of the live list is chosen. `0` while the screen is shut. */
  private at = 0;

  /** Which shape it is in. `null` while it is shut. */
  private showing: TitlePage | null = null;

  constructor(root: HTMLElement) {
    this.element = document.createElement("div");
    this.element.className = "hud__title";
    this.element.hidden = true;

    this.scrim = document.createElement("div");
    this.scrim.className = "hud__title-scrim";

    // The thin frame around the whole screen: the lab's candidate signature for
    // full-screen states, shipped on this one only (see `ui/title.css`). Its own
    // element rather than a border on the layer, because it is inset from the
    // edge and carries two corner diamonds of its own.
    const frame = document.createElement("div");
    frame.className = "hud__title-frame";

    const word = document.createElement("h1");
    // The kit's deep display shadow, composed alongside (plan D2): this is the
    // largest type in the client and it stands on whatever is behind the scrim.
    word.className = "hud__title-word hud-text--display";
    word.textContent = WORDMARK;

    // The gold flourish under it, as the kit's rule-diamond: two fading
    // hairlines with a diamond between them. `ui/title.css` narrows it and
    // moves it to the left, which is the whole of the difference from the
    // reading window's centred one.
    const flourish = document.createElement("div");
    flourish.className = "hud__title-flourish rule-diamond";
    flourish.setAttribute("aria-hidden", "true");

    const diamond = document.createElement("span");
    diamond.className = "objective-diamond";
    flourish.append(diamond);

    this.itemsElement = document.createElement("nav");
    this.itemsElement.className = "hud__title-items";
    // A menu of one choice, so the label is what it is. Lower case, like every
    // other string this client shows.
    this.itemsElement.setAttribute("aria-label", "the main menu");

    this.hintElement = document.createElement("div");
    this.hintElement.className = "hud__title-hint";

    this.saidElement = document.createElement("p");
    this.saidElement.className = "hud__title-said";
    this.saidElement.hidden = true;

    this.fileElement = document.createElement("input");
    this.fileElement.type = "file";
    // What the chooser offers first. A replay is JSON and is written with a
    // `.json` name (`host/download.ts`), and both spellings are given because a
    // browser matches the extension and an operating system matches the type.
    this.fileElement.accept = "application/json,.json";
    this.fileElement.className = "hud__title-file";
    this.fileElement.hidden = true;

    this.rail = document.createElement("div");
    this.rail.className = "hud__title-rail";
    this.rail.append(
      word,
      flourish,
      this.itemsElement,
      this.saidElement,
      this.hintElement,
      this.fileElement,
    );

    this.pageElement = document.createElement("div");
    this.pageElement.className = "hud__title-page";
    this.pageElement.hidden = true;

    this.element.append(this.scrim, frame, this.rail, this.pageElement);
    root.append(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /** Which shape it is in, or `null` while it is shut. */
  get page(): TitlePage | null {
    return this.showing;
  }

  /** Which line of the live list is chosen. */
  get cursor(): number {
    return this.at;
  }

  /**
   * Draws the screen.
   *
   * Called again for every move, like every other surface in this HUD: a view
   * goes in and the page comes out of it, with no caller ever told which part
   * changed. The rail's `<button>`s are the one thing that survives a redraw —
   * see {@link TitleScreen.drawn} for the bug that rule exists for.
   */
  show(view: TitleView): void {
    const live = view.page === "settings" ? view.rows.length : view.items.length;

    this.showing = view.page;
    this.at = Math.min(Math.max(view.cursor, 0), Math.max(live - 1, 0));

    const words = view.items.map((item) => `${item.item}:${item.word}`).join("\n");

    if (words !== this.drawn) {
      this.itemsElement.replaceChildren(...view.items.map((item) => this.renderItem(item)));
      this.drawn = words;
    }

    for (const [index, element] of [
      ...this.itemsElement.querySelectorAll<HTMLElement>(".hud__title-item"),
    ].entries()) {
      this.dressItem(element, index, view);
    }

    // The rows are rebuilt rather than dressed, and the difference between them
    // and the items is what a redraw is *for* here: an item's word never
    // changes and a row's value changes every time somebody presses ← or →, so
    // there is nothing to preserve and a value written into a stale node is the
    // one bug this surface could actually have.
    this.pageElement.replaceChildren(
      ...view.rows.map((row, index) => this.renderRow(row, index, view)),
    );
    this.pageElement.hidden = view.page !== "settings";

    this.hintElement.replaceChildren(...view.keys.map((key) => hintOf(key)));
    this.saidElement.textContent = view.said;
    this.saidElement.hidden = view.said === "";
    // Rebound with the view like the items above, and for the same reason: the
    // callback belongs to whoever opened the screen. The read is `text()` — a
    // promise per file rather than a `FileReader` and three events — and a
    // browser that refuses to read it says so on the same line a bad replay
    // does (`session/restore.ts`'s `UNREADABLE_FILE`).
    this.fileElement.onchange = (): void => {
      const file = this.fileElement.files?.[0] ?? null;

      if (file === null) {
        return;
      }

      void file
        .text()
        .then((text) => {
          view.onFile(text);
        })
        .catch(() => {
          // The empty string is *unreadable*, which the flow spells out: a
          // browser that will not hand over a file somebody chose is a refusal
          // like any other and is said on the same line.
          view.onFile("");
        });
    };
    this.element.hidden = false;
  }

  hide(): void {
    this.element.hidden = true;
    this.showing = null;
  }

  /**
   * Opens the browser's file chooser.
   *
   * Called from inside the press that asked for it, which is the whole
   * constraint: a chooser is opened by a click on the input and by nothing else,
   * so this is a `.click()` and not a permission this file could ask for any
   * other way. The value is cleared first so that choosing the same file twice
   * — after a refusal, which is exactly when somebody would — raises `change`
   * the second time as well.
   */
  askForAFile(): void {
    this.fileElement.value = "";
    this.fileElement.click();
  }

  /** One word on the rail, built once. */
  private renderItem(item: TitleItemView): HTMLElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = "hud__title-item";
    // The door it opens, so a check and a driver can select the row by what it
    // *does* rather than by the word on it — the menu strip's `data-page`, one
    // surface over (`hud/panels/menu.ts`).
    element.dataset.item = item.item;
    // Typed in lower case and uppercased by the stylesheet, never in the string:
    // `ui/title.css` carries the one `text-transform` this chunk ships and the
    // check tree asserts this text is still the word it was written as.
    element.textContent = item.word;

    return element;
  }

  /** And what changes about it: the gold box, and what pressing it means now. */
  private dressItem(element: HTMLElement, index: number, view: TitleView): void {
    const item = view.items[index];

    if (item === undefined) {
      return;
    }

    // On the rail the box follows the cursor; on the settings page it stays on
    // the word whose page is open, so the screen says where the reader is even
    // though the live list is somewhere else.
    const chosen = view.page === "rail" ? index === this.at : item.item === "settings";

    element.classList.toggle("hud__title-item--at", chosen);
    element.setAttribute("aria-current", chosen ? "true" : "false");
    // Rebound with the view, because the callback belongs to whoever opened the
    // screen rather than to the element that survived the last draw.
    element.onclick = (event: MouseEvent): void => {
      event.stopPropagation();

      // A word on the rail is always the door it names, on either page: a
      // player reading the settings who presses *begin* meant *begin*.
      view.onItem(item.item);
    };
    element.onmouseenter = (): void => {
      if (view.page === "rail") {
        view.onCursor(index);
      }
    };
  }

  /** One row of the settings page. */
  private renderRow(row: TitleRowView, index: number, view: TitleView): HTMLElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = "hud__title-row";
    element.dataset.row = row.row;

    if (view.page === "settings" && index === this.at) {
      element.classList.add("hud__title-row--at");
    }

    const name = document.createElement("span");
    name.className = "hud__title-row-name";
    name.textContent = row.name;
    element.append(name);

    if (row.value !== "") {
      const value = document.createElement("span");
      value.className = "hud__title-row-value";
      value.textContent = row.value;
      element.append(value);
    }

    if (row.note !== "") {
      const note = document.createElement("span");
      note.className = "hud__title-row-note";
      note.textContent = row.note;
      element.append(note);
    }

    element.onclick = (event: MouseEvent): void => {
      event.stopPropagation();
      view.onCursor(index);
      view.onChoose(index);
    };
    element.onmouseenter = (): void => {
      view.onCursor(index);
    };

    return element;
  }
}

/**
 * Puts the title screen up and resolves when the player has chosen to begin.
 *
 * The awaited call at the head of `main.ts`'s start sequence, before
 * {@link openTheBook}. **Everything asynchronous about it is the wait for a
 * person**: the screen reads nothing and holds nothing, so it blocks a boot and
 * never a frame — and the frame loop has not started, which is the same fact
 * every beat of the opening is written under.
 *
 * `?title=0` returns `"declined"` without drawing anything at all.
 */
export async function showTheTitle(
  root: HTMLElement,
  search: string,
  touch = false,
  doors: TitleDoors = {},
): Promise<TitleRead> {
  if (!titleRequested(search)) {
    return "declined";
  }

  const screen = new TitleScreen(root);
  let read: TitleRead = "begun";

  await new Promise<void>((resolve) => {
    let page: TitlePage = "rail";
    let cursor = 0;
    let choice = storedTouchChoice();
    let done = false;
    // What the screen is saying under the rail: a refused file, or nothing.
    let said = "";
    // Whether a file is being read and re-run behind this screen. Every gesture
    // is refused while it is: the rail is hidden — the wait belongs to whoever
    // opened the door and is drawn over the dark — and a second file chosen
    // mid-restore would be two worlds being built at once.
    let loading = false;
    const items = railItems(doors.load !== undefined);

    const settle = (how: TitleRead): void => {
      if (done) {
        return;
      }

      done = true;
      read = how;
      document.removeEventListener("keydown", onKey);
      screen.hide();
      resolve();
    };

    /** How many lines the live list has, which is what the cursor is clamped to. */
    const live = (): number =>
      page === "settings" ? settingsRows(choice, search).length : items.length;

    const draw = (): void => {
      screen.show({
        page,
        items,
        rows: page === "settings" ? settingsRows(choice, search) : [],
        cursor,
        keys: keysFor(page, touch),
        said,
        onFile: (text: string) => {
          void takeFile(text);
        },
        onItem: (item: TitleItem) => {
          takeItem(item);
        },
        onChoose: (at: number) => {
          takeRow(at);
        },
        onCursor: (at: number) => {
          const wanted = Math.min(Math.max(at, 0), Math.max(live() - 1, 0));

          // Nothing is redrawn for a cursor that did not move, and it is a
          // guard rather than an economy: the cursor follows the pointer here,
          // so a draw that replaced the row under the mouse would raise
          // `mouseenter` on the new one and ask for the same draw again.
          if (wanted === cursor) {
            return;
          }

          cursor = wanted;
          draw();
        },
      } satisfies TitleView);
    };

    /** What a word on the rail does. */
    const takeItem = (item: TitleItem): void => {
      if (loading) {
        return;
      }

      if (item === "begin") {
        settle("begun");

        return;
      }

      if (item === "load") {
        // Nothing is settled and nothing is said yet: the browser is asked for a
        // file, and a player who closes the chooser is standing where they were.
        // A previous refusal goes now rather than when the next one arrives, so
        // the screen is never showing a sentence about a file that has been
        // replaced.
        said = "";
        draw();
        screen.askForAFile();

        return;
      }

      if (item === "settings") {
        page = "settings";
        cursor = 0;
        draw();

        return;
      }

      // *leave*, which is only ever drawn where it can do something
      // ({@link canLeave}). Nothing is settled: if the window refuses to close,
      // the screen is still up and the player has lost nothing.
      window.close();
    };

    /**
     * And what a chosen file does: the screen steps aside while the door works.
     *
     * The rail is hidden for the whole of it, because what is behind this screen
     * during a load is the caller's own wait — the loading line, counting the
     * ticks it is re-running (`main.ts`, `session/restore.ts`) — and a title
     * screen over it would be a menu on top of a progress line. It comes back
     * with the refusal if there is one, which is the only reason it is hidden
     * rather than settled.
     */
    const takeFile = async (text: string): Promise<void> => {
      const load = doors.load;

      if (done || loading || load === undefined) {
        return;
      }

      loading = true;
      screen.hide();

      const refusal = text === "" ? UNREADABLE_FILE : await load(text);

      loading = false;

      if (refusal === null) {
        settle("loaded");

        return;
      }

      said = refusal;
      draw();
    };

    /** And what a row of the settings page does. */
    const takeRow = (at: number): void => {
      const row = settingsRows(choice, search)[at];

      if (row === undefined || row.row === "back") {
        page = "rail";
        // Back to the word that opened the page rather than to the top of the
        // rail: a player who left the settings is standing where they left from.
        cursor = Math.max(
          items.findIndex((item) => item.item === "settings"),
          0,
        );
        draw();

        return;
      }

      cycle(1);
    };

    /** Moves the touch row's value along, and remembers it. */
    const cycle = (step: number): void => {
      const row = settingsRows(choice, search)[cursor];

      if (page !== "settings" || row?.row !== "touch") {
        return;
      }

      const was = TOUCH_CHOICE_ORDER.indexOf(choice);
      const next = TOUCH_CHOICE_ORDER[
        (was + step + TOUCH_CHOICE_ORDER.length) % TOUCH_CHOICE_ORDER.length
      ];

      if (next === undefined) {
        return;
      }

      choice = next;
      storeTouchChoice(choice);
      draw();
    };

    const onKey = (event: KeyboardEvent): void => {
      // Nothing is walked while a file is being re-run: the rail is not on the
      // screen, and a key that moved an invisible cursor would land somewhere
      // nobody chose the moment a refusal put it back up.
      if (loading) {
        return;
      }

      // **A digit or a letter typed into the one text field in this client is
      // part of the name** — the lab's rule, stated against every key handler
      // the start sequence owns (`hud/opening.ts`, `hud/origins.ts`). Nothing
      // can reach here from the field today (the field is three beats away and
      // stops its own events anyway); the guard is written all the same,
      // because a rule that only holds because of what the boot order happens
      // to be is a rule the next reordering silently breaks.
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      if (event.key === "Escape") {
        // **Refused on the rail**, which is U-3's ruling one surface over: no
        // surface in this client closes on Escape, and there is nothing behind
        // this one to close *to*. On the settings page it is the way back,
        // which is a different question — the page has somewhere to return to
        // and the row beside it says the same thing.
        if (page !== "settings") {
          return;
        }

        event.preventDefault();
        takeRow(settingsRows(choice, search).findIndex((row) => row.row === "back"));

        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();

        if (page === "settings") {
          takeRow(cursor);

          return;
        }

        const item = items[cursor];

        if (item !== undefined) {
          takeItem(item.item);
        }

        return;
      }

      const sideways = changeFor(event);

      if (sideways !== 0) {
        event.preventDefault();
        cycle(sideways);

        return;
      }

      const step = moveFor(event);

      if (step === 0) {
        return;
      }

      event.preventDefault();

      // **Wrapped rather than clamped**, which is the opposite of what the book
      // and the strip of lives do, and the difference is what is being walked.
      // Those two clamp because the page somebody is on is the page they meant
      // to be on and a book that ejected a reader for pressing on would punish
      // reading it to the end. This is a rail of two or three words: a ↓ that
      // did nothing on the last of them would be a key that works half the time
      // on the first screen of the game.
      const count = live();

      cursor = count === 0 ? 0 : (cursor + step + count) % count;
      draw();
    };

    document.addEventListener("keydown", onKey);
    draw();
  });

  return read;
}

/**
 * Whether this session opens on the title screen: `?title=0` says it does not.
 *
 * **Default on, and gated on the URL alone** — never on `import.meta.env.DEV`,
 * which is O7's rule and the trap it names: the owner playtests the *deployed*
 * preview, which is a production build, so a first frame that only existed in
 * development would be a first frame nobody ever judged.
 *
 * **It is a harness switch, not an option** (`docs/decisions/devtools.md` §2's
 * distinction, and this chunk's inventory of every flag in the client): the
 * three drivers open eleven sessions between them and each of them would
 * otherwise pay for a screen that has to be walked through by hand. One session
 * per driver goes through it and proves it; the rest say `?title=0`. It is the
 * same category as `?fixture=1` and lives beside it in `web/README.md`.
 *
 * The spelling is `session/params.ts`'s with the default inverted, exactly like
 * `?book=` and `?born=`: the screen is the shipped behaviour and the parameter
 * is how somebody declines it.
 */
export function titleRequested(search: string): boolean {
  const raw = new URLSearchParams(search).get(TITLE_PARAM);

  if (raw === null) {
    return true;
  }

  const value = raw.trim().toLowerCase();

  return value !== "0" && value !== "false" && value !== "off";
}

/**
 * Whether a *leave* row would do anything, which is the only reason it is drawn.
 *
 * `window.close()` is refused by every browser for a tab the player opened
 * themselves; it works for one a script opened, which is exactly what
 * `window.opener` being set means. So the row appears in a window this page was
 * opened into and nowhere else on the web — **absent rather than greyed**, which
 * is D10's rule about *load* and *continue* applied to the one item that has a
 * platform answer instead of a schedule.
 *
 * **This is the seam a wrapped build widens.** A Steam build is a window a
 * launcher owns and *leave* is meaningful there (plan D10); when that shell
 * exists, this function is the one place that learns about it.
 */
export function canLeave(): boolean {
  return window.opener !== null;
}

/**
 * What the touch setting is remembered as, or `auto` when nothing has been
 * chosen — and when the browser will not say.
 *
 * **Read through the argument rather than through `window`**, on
 * `session/params.ts`'s rule: the awkward cases are checked without a browser to
 * set up, and the awkward case here is a `localStorage` that *throws*. It does,
 * on a real device: Safari's private browsing and a third-party frame with
 * storage blocked both raise on the property access as well as on the call, and
 * the self-checks run in a page where either could be true.
 *
 * Anything that is not one of the three words is `auto` — a key somebody edited
 * by hand, a value written by an older build, an empty string. The shipped
 * behaviour is the one a broken setting falls back to, which is the shape every
 * URL parameter in this client already has (`session/params.ts`'s `?decay=`).
 */
export function storedTouchChoice(store: Pick<Storage, "getItem"> | null = browserStore()): TouchChoice {
  try {
    const raw = store?.getItem(TOUCH_SETTING_KEY) ?? null;

    return TOUCH_CHOICE_ORDER.find((choice) => choice === raw) ?? "auto";
  } catch {
    return "auto";
  }
}

/**
 * Remembers the touch choice, per browser, and says nothing if it cannot.
 *
 * **It is remembered rather than applied**, and the row says so
 * ({@link TOUCH_ROW_NOTE}). The mode is decided once in `main.ts`'s boot
 * sequence and handed to everything that needs it — the key card, the panels'
 * close hints, the root scale's floor, the aiming prompt and `Controls` — and
 * three of those five are built *before* this screen is drawn. Changing the
 * answer here would dress half a session in one mode and half in the other,
 * which is a worse lie than a sentence saying when it takes hold. The
 * alternative, reloading the page from a settings row, was refused: a settings
 * page that can throw a session away is one nobody will open twice, and this
 * screen is meant to be reachable from a running session one day.
 *
 * A failed write is silent. There is nothing useful to say to a player about a
 * browser that will not remember a preference, and the setting still governs
 * this screen for as long as it is up.
 */
export function storeTouchChoice(
  choice: TouchChoice,
  store: Pick<Storage, "setItem"> | null = browserStore(),
): void {
  try {
    store?.setItem(TOUCH_SETTING_KEY, choice);
  } catch {
    // Nothing. See above.
  }
}

/**
 * The setting as `touchMode` wants to be asked: `null` is *ask the device*.
 *
 * The one line that joins the settings page to the decision, and it is a pure
 * translation so that `input/touch.ts` stays a file with no storage in it
 * (`touchDeviceSaysYes` is the same arrangement for the device).
 */
export function touchChoiceSaysYes(choice: TouchChoice): boolean | null {
  if (choice === "auto") {
    return null;
  }

  return choice === "on";
}

/**
 * The rail's words: what is true today, in the order the lab drew them.
 *
 * `loadable` is whether a caller handed over the door — it is drawn where it
 * opens and nowhere else, which is the rule *leave* has always been under
 * ({@link canLeave}).
 *
 * **Still no *continue*** (D10). Nothing in this client remembers a session by
 * itself, so a row offering to resume one would be a promise the browser never
 * made; the session that *can* come back is a file, and that is the row above.
 * It is asserted *absent* in `dev/selfcheck/title.ts`, the way
 * `hud__panel--deed` is, because an item quietly appearing is exactly the kind
 * of thing nobody notices.
 */
export function railItems(loadable = false): readonly TitleItemView[] {
  const items: TitleItemView[] = [{ item: "begin", word: BEGIN_WORD }];

  if (loadable) {
    items.push({ item: "load", word: LOAD_WORD });
  }

  items.push({ item: "settings", word: SETTINGS_WORD });

  if (canLeave()) {
    items.push({ item: "leave", word: LEAVE_WORD });
  }

  return items;
}

/**
 * The settings page's rows: **only options that exist, and only player-facing
 * ones.**
 *
 * One setting, which is the honest count. There is no audio in this client, so
 * there is no volume; there is nothing about the renderer a player may choose,
 * so there is no graphics section; and the day/night scrubber, the pacing knobs
 * and the self-check verdict are **instruments** rather than options — they ride
 * `?dev=1` and F3 and stay there (`docs/decisions/devtools.md` §2: a developer
 * instrument is not a setting because it is asking a different question).
 *
 * The plan's own sentence for this chunk names *"the pacing knobs, self-checks
 * toggle"* among the rows and this refuses them for that reason; the flag
 * inventory that settles which is which is in `docs/decisions/ui.md` §7.
 */
export function settingsRows(choice: TouchChoice, search: string): readonly TitleRowView[] {
  return [
    {
      row: "touch",
      name: TOUCH_ROW_NAME,
      value: TOUCH_CHOICE_WORDS[choice],
      note: touchRequested(search) === null ? TOUCH_ROW_NOTE : TOUCH_ROW_OVERRIDDEN,
    },
    { row: "back", name: BACK_ROW_NAME, value: "", note: "" },
  ];
}

/** Which foot line a page wears, and which spelling of it this session reads. */
export function keysFor(page: TitlePage, touch: boolean): readonly TitleHint[] {
  if (page === "settings") {
    return touch ? SETTINGS_TAPS : SETTINGS_KEYS;
  }

  return touch ? RAIL_TAPS : RAIL_KEYS;
}

/** `localStorage`, or `null` where the browser will not hand it over. */
function browserStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // The property access itself throws in a frame with storage blocked, which
    // is why this is a function and not a constant.
    return null;
  }
}

/** One chip and the word beside it, or one word on its own. */
function hintOf(key: TitleHint): HTMLElement {
  const element = document.createElement("span");
  element.className = "hud__title-hint-key";

  if (key.press !== null) {
    const chip = document.createElement("kbd");
    // The client's own plate on the kit's chip, for chunk U-1's reason: the
    // kit's `kbd` background is a *lightening*, which reads as a key on a dark
    // panel and vanishes over anything pale.
    chip.className = "hud__title-key";
    chip.textContent = key.press;
    element.append(chip);
  }

  if (key.does !== "") {
    const said = document.createElement("span");
    said.textContent = key.does;
    element.append(said);
  }

  return element;
}

/**
 * Which way a key moves the cursor.
 *
 * **The arrows by `key` and the letters by `code`**, which is the split the two
 * halves of this client already keep: `hud/opening.ts` turns pages by `key`
 * because ↑ and ↓ mean *next* and *previous* on every keyboard whatever is
 * printed on them, and `input/controls.ts` binds letters by `code` because W
 * has to be the key under the finger on an AZERTY board. This screen has both,
 * so it asks both questions.
 */
function moveFor(event: KeyboardEvent): number {
  if (event.key === "ArrowDown" || event.code === "KeyS") {
    return 1;
  }

  if (event.key === "ArrowUp" || event.code === "KeyW") {
    return -1;
  }

  return 0;
}

/** And which way a key moves a value along its own list of words. */
function changeFor(event: KeyboardEvent): number {
  if (event.key === "ArrowRight" || event.code === "KeyD") {
    return 1;
  }

  if (event.key === "ArrowLeft" || event.code === "KeyA") {
    return -1;
  }

  return 0;
}
